import { ELoadingType } from '@aca-new/app/shared/models/enums/loading-type.enum';
import { QimaColorService } from '@aca-new/app/shared/services/exported-services/qima-color/qima-color.service';
import { ChangeDetectorRef, ComponentRef, Directive, ElementRef, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges, Type, ViewContainerRef } from '@angular/core';
import { EQimaThreeSquaresLoaderStyle, QimaSpinnerComponent, QimaThreeSquaresLoaderComponent } from '@qima/ngx-qima';
import { has } from 'lodash/index';

@Directive({
  selector: '[appLoading]',
  standalone: true,
})
export class LoadingDirective implements OnChanges, OnDestroy {
  /**
   * @description
   * Has loading or not
   * @type {boolean}
   * @default false
   */
  @Input('appLoading')
  public isLoading: boolean = false;

  /**
   * @description
   * Loading type, Only support SPINNER and THREE_SQUARES now
   * @type {ELoadingType}
   * @default ELoadingType.SPINNER
   */
  @Input('appLoadingType')
  public loadingType: ELoadingType = ELoadingType.SPINNER;

  /**
   * @description
   * The loading icon size
   * @type {number}
   * @default 48
   */
  @Input('appLoadingSize')
  public size: number = 48;

  private _loadingComponentRef: ComponentRef<QimaSpinnerComponent | QimaThreeSquaresLoaderComponent> | null = null;
  private _overlayDiv: HTMLElement | null = null;

  public constructor(
    private readonly _el: ElementRef,
    private readonly _renderer: Renderer2,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _qimaColor: QimaColorService
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    this._onIsLoadingChange(changes);
  }

  public ngOnDestroy(): void {
    this._hideLoading();
  }

  private _onIsLoadingChange(changes: SimpleChanges): void {
    if (has(changes, 'isLoading') && changes.isLoading.currentValue !== changes.isLoading.previousValue) {
      if (this.isLoading) {
        this._showLoading();
      } else {
        this._hideLoading();
      }

      this._changeDetectorRef.markForCheck();
    }
  }

  private _showLoading(): void {
    this._hideLoading();
    const hostElement = this._el.nativeElement;

    this._renderer.setStyle(hostElement, 'position', 'relative');
    this._overlayDiv = this._renderer.createElement('div');
    this._renderer.addClass(this._overlayDiv, 'app-loading');
    this._renderer.setStyle(this._overlayDiv, 'position', 'absolute');
    this._renderer.setStyle(this._overlayDiv, 'top', '0');
    this._renderer.setStyle(this._overlayDiv, 'left', '0');
    this._renderer.setStyle(this._overlayDiv, 'width', '100%');
    this._renderer.setStyle(this._overlayDiv, 'height', '100%');
    this._renderer.setStyle(this._overlayDiv, 'backgroundColor', this._qimaColor.neutral.NEUTRAL_WHITE);
    this._renderer.setStyle(this._overlayDiv, 'display', 'flex');
    this._renderer.setStyle(this._overlayDiv, 'alignItems', 'center');
    this._renderer.setStyle(this._overlayDiv, 'justifyContent', 'center');
    this._renderer.setStyle(this._overlayDiv, 'zIndex', '1001');
    this._renderer.setStyle(this._overlayDiv, 'pointerEvents', 'all');
    this._renderer.setStyle(this._overlayDiv, 'opacity', '0.9');

    this._renderer.appendChild(hostElement, this._overlayDiv);

    let componentType: Type<QimaSpinnerComponent | QimaThreeSquaresLoaderComponent>;

    if (this.loadingType === ELoadingType.THREE_SQUARES) {
      componentType = QimaThreeSquaresLoaderComponent;
    } else {
      componentType = QimaSpinnerComponent;
    }

    this._loadingComponentRef = this._viewContainerRef.createComponent(componentType);
    const componentInstance = this._loadingComponentRef.instance;

    componentInstance.size = this.size;

    if (this.loadingType === ELoadingType.THREE_SQUARES) {
      if (!(componentInstance instanceof QimaSpinnerComponent)) {
        componentInstance.style = EQimaThreeSquaresLoaderStyle.POOL_500;
      }
    } else {
      this._renderer.setStyle(this._loadingComponentRef?.location.nativeElement, 'color', this._qimaColor.pool.POOL_500);
    }

    this._renderer.appendChild(this._overlayDiv, this._loadingComponentRef.location.nativeElement);
  }

  private _hideLoading(): void {
    if (this._loadingComponentRef) {
      this._loadingComponentRef.destroy();
      this._loadingComponentRef = null;
    }

    if (this._overlayDiv) {
      this._renderer.removeChild(this._el.nativeElement, this._overlayDiv);
      this._overlayDiv = null;
    }
  }
}
